import React, {
  Fragment,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { map, prop, sortBy } from "ramda";
import { UserContext } from "UserContext";
import {
  FormControlLabel,
  Grid,
  RadioGroup,
  Radio,
  makeStyles,
} from "@material-ui/core";
import { fetchReport, saveReportResponse } from "fetchUtils";
import ReportsPickerCard from "./ReportsPickerCard";
import ReportsCard from "./ReportsCard";
import OrganizationReportsPickerCard from "./OrganizationReportsPickerCard";
import LoadingButton from "./LoadingButton";

const getModuleUrl = userId =>
  `${process.env.REACT_APP_API_BASE_URL}/users/${userId}/modules?limit=all`;

const getModuleReportUrl = props =>
  `${process.env.REACT_APP_API_BASE_URL}/reports/inventory/report.csv?media-id=${props.selectedModule}`;

const getOrganizationReportUrl = props =>
  `${process.env.REACT_APP_API_BASE_URL}/reports/inventory/report.csv?org-id=${props.selectedOrganization}`;

const fetchModules = (userId, token) =>
  fetch(getModuleUrl(userId), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

const mapModule = module => ({ id: module.id, name: module.displayName });

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.secondary.light,
    height: "100%",
    overflowX: "hidden",
  },
  exportButton: {
    width: 120,
    height: 40,
    marginBottom: 15,
    float: "right",
    right: 15,
  },
  radioGroup: {
    justifyContent: "space-around",
    marginBottom: 15,
  },
}));

const initialState = {
  modules: [],
  selectedModule: "",
  showLoading: false,
  selectedOrganization: "",
  inventoryGroup: "organization",
};

const InventoryReports = () => {
  const classes = useStyles();
  const { token, user } = useContext(UserContext);
  const [state, setState] = useState(initialState);

  const handleStateChange = name => value => {
    setState(s => ({ ...s, [name]: value }));
  };

  const handleSelectedOrganization = useCallback(value => {
    handleStateChange("selectedOrganization")(value);
  }, []);

  useEffect(() => {
    if (token) {
      fetchModules(user.id, token)
        .then(response => response.json())
        .then(modules => map(mapModule, modules))
        .then(modules => sortBy(prop("name"), modules))
        .then(modules => {
          handleStateChange("modules")(modules);

          if (modules.length > 0) {
            handleStateChange("selectedModule")(modules[0].id);
          }
        });
    } else {
      handleStateChange("modules")([]);
    }
  }, [token, user.id]);

  const exportReport = props => {
    handleStateChange("showLoading")(true);

    const url =
      props.inventoryGroup === "organization"
        ? getOrganizationReportUrl(props)
        : getModuleReportUrl(props);

    fetchReport(url, props.token)
      .then(response => saveReportResponse(response, "inventory-report.csv"))
      .finally(() => handleStateChange("showLoading")(false));
  };

  return (
    <Fragment>
      {state.modules.length < 1 ? (
        <div>Loading...</div>
      ) : (
        <Grid item xs>
          <ReportsCard cardLabel="INVENTORY GROUPING">
            <RadioGroup
              row
              className={classes.radioGroup}
              value={state.inventoryGroup}
              onChange={e =>
                handleStateChange("inventoryGroup")(e.target.value)
              }
            >
              <FormControlLabel
                value="organization"
                control={<Radio />}
                label="Organization"
              />
              <FormControlLabel
                value="module"
                control={<Radio />}
                label="Module"
              />
            </RadioGroup>
          </ReportsCard>
          <br />
          {state.inventoryGroup === "organization" ? (
            <OrganizationReportsPickerCard
              organizations={user.organizations}
              onOrganizationChange={handleSelectedOrganization}
            >
              <LoadingButton
                variant="contained"
                disableElevation
                className={classes.exportButton}
                onClick={() => exportReport({ token, ...state })}
                // disabled={props.disableExport}
                showLoading={state.showLoading}
              >
                EXPORT
              </LoadingButton>
            </OrganizationReportsPickerCard>
          ) : (
            <ReportsPickerCard
              label="MODULE"
              values={state.modules}
              selectedValue={state.selectedModule}
              onValueChange={handleStateChange("selectedModule")}
            >
              <LoadingButton
                variant="contained"
                disableElevation
                className={classes.exportButton}
                onClick={() => exportReport({ token, ...state })}
                // disabled={props.disableExport}
                showLoading={state.showLoading}
              >
                EXPORT
              </LoadingButton>
            </ReportsPickerCard>
          )}
        </Grid>
      )}
    </Fragment>
  );
};

export default InventoryReports;
